import React, { useEffect, useState } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Button,
} from '@mui/material';
import {
  QuestionAnswer,
  Quiz,
  People,
  AttachMoney,
  TrendingUp,
  CheckCircle,
  PendingActions,
  ArrowUpward,
  ArrowDownward,
  Download,
} from '@mui/icons-material';
import { api } from '../services/api';

interface DashboardData {
  questions: {
    total: number;
    by_status: Array<{ status: string; count: number }>;
  };
  exams: {
    total: number;
    total_attempts: number;
  };
  users: {
    total_parents: number;
    total_students: number;
  };
  revenue: {
    total: number;
    this_month: number;
    today: number;
    pending: {
      amount: number;
      count: number;
    };
  };
  admin_users: number;
  recent_activity: Array<{ date: string; registrations: number }>;
}

type GradientKey = 'teal' | 'gold' | 'rose' | 'sky' | 'success' | 'warning';

const GRADIENTS: Record<GradientKey, { bg: string; fg: string }> = {
  teal:    { bg: 'linear-gradient(135deg, #338275 0%, #1F5449 100%)', fg: '#FFFFFF' },
  gold:    { bg: 'linear-gradient(135deg, #F4D06F 0%, #D4A017 100%)', fg: '#4A3600' },
  rose:    { bg: 'linear-gradient(135deg, #F472B6 0%, #DB2777 100%)', fg: '#FFFFFF' },
  sky:     { bg: 'linear-gradient(135deg, #38BDF8 0%, #0284C7 100%)', fg: '#FFFFFF' },
  success: { bg: 'linear-gradient(135deg, #34D399 0%, #059669 100%)', fg: '#FFFFFF' },
  warning: { bg: 'linear-gradient(135deg, #FBBF24 0%, #D97706 100%)', fg: '#FFFFFF' },
};

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  gradient: GradientKey;
  subtitle?: string;
  delta?: { value: string; up?: boolean };
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, gradient, subtitle, delta }) => {
  const g = GRADIENTS[gradient];
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        height: '100%',
        borderRadius: 3,
        border: '1px solid',
        borderColor: '#F0F2F1',
        backgroundColor: '#FFFFFF',
        boxShadow: '0 1px 2px rgba(26, 58, 52, 0.04)',
      }}
    >
      <Box
        sx={{
          width: 44, height: 44, borderRadius: 3,
          background: g.bg, color: g.fg,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          mb: 1.5,
        }}
      >
        {icon}
      </Box>
      <Typography
        variant="caption"
        sx={{
          color: 'text.disabled',
          fontWeight: 700,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          fontSize: 11,
        }}
      >
        {title}
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 800, my: 0.5, color: 'text.primary' }}>
        {value}
      </Typography>
      {delta ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {delta.up ? (
            <ArrowUpward sx={{ fontSize: 14, color: 'success.main' }} />
          ) : (
            <ArrowDownward sx={{ fontSize: 14, color: 'error.main' }} />
          )}
          <Typography
            variant="caption"
            sx={{ color: delta.up ? 'success.main' : 'error.main', fontWeight: 700 }}
          >
            {delta.value}
          </Typography>
        </Box>
      ) : subtitle ? (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {subtitle}
        </Typography>
      ) : null}
    </Paper>
  );
};

const Dashboard: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const response = await api.analytics.getDashboard();
      setData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return <Alert severity="warning">No data available</Alert>;

  const approvedQuestions = data.questions.by_status.find((s) => s.status === 'approved')?.count || 0;
  const pendingQuestions = data.questions.by_status.find((s) => s.status === 'pending_review')?.count || 0;
  const totalUsers = data.users.total_parents + data.users.total_students;

  const statusColor = (status: string) =>
    status === 'approved' ? 'success'
    : status === 'pending_review' ? 'warning'
    : status === 'rejected' ? 'error'
    : 'disabled';

  return (
    <Box>
      {/* Page head */}
      <Box sx={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end',
        justifyContent: 'space-between', gap: 2, mb: 3,
      }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.01em', color: 'text.primary' }}>
            Dashboard
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            Welcome back — here's what's happening today.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Chip label="Last 7 days ▾" variant="outlined" sx={{ fontWeight: 600 }} />
          <Button startIcon={<Download />} variant="outlined" size="small" sx={{ fontWeight: 600 }}>
            Export
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Questions"
            value={data.questions.total.toLocaleString()}
            icon={<QuestionAnswer />}
            gradient="teal"
            subtitle={`${approvedQuestions.toLocaleString()} approved`}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Pending Review"
            value={pendingQuestions.toLocaleString()}
            icon={<PendingActions />}
            gradient="warning"
            subtitle="Needs verification"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Exams"
            value={data.exams.total.toLocaleString()}
            icon={<Quiz />}
            gradient="rose"
            subtitle={`${data.exams.total_attempts.toLocaleString()} attempts`}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Users"
            value={totalUsers.toLocaleString()}
            icon={<People />}
            gradient="sky"
            subtitle={`${data.users.total_parents} parents · ${data.users.total_students} students`}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Revenue"
            value={`₹${data.revenue.total.toLocaleString()}`}
            icon={<AttachMoney />}
            gradient="success"
            subtitle="All time"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="This Month"
            value={`₹${data.revenue.this_month.toLocaleString()}`}
            icon={<TrendingUp />}
            gradient="gold"
            subtitle="Current month"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Today"
            value={`₹${data.revenue.today.toLocaleString()}`}
            icon={<AttachMoney />}
            gradient="teal"
            subtitle="Today's earnings"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Pending Payments"
            value={`₹${data.revenue.pending.amount.toLocaleString()}`}
            icon={<PendingActions />}
            gradient="warning"
            subtitle={`${data.revenue.pending.count} transactions`}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3, borderRadius: 3,
              border: '1px solid',
              borderColor: '#F0F2F1',
              boxShadow: '0 1px 2px rgba(26, 58, 52, 0.04)',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Question status breakdown
              </Typography>
              <Chip label={`${data.questions.total} total`} size="small" sx={{ fontWeight: 600 }} />
            </Box>
            <Box>
              {data.questions.by_status.map((statusData) => {
                const pct = data.questions.total > 0
                  ? (statusData.count / data.questions.total) * 100
                  : 0;
                return (
                  <Box
                    key={statusData.status}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 1.25,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:last-child': { borderBottom: 'none' },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flex: 1 }}>
                      <CheckCircle fontSize="small" color={statusColor(statusData.status) as any} />
                      <Typography sx={{ textTransform: 'capitalize', fontWeight: 500, flex: 1 }}>
                        {statusData.status.replace('_', ' ')}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 40, textAlign: 'right' }}>
                        {pct.toFixed(0)}%
                      </Typography>
                    </Box>
                    <Typography fontWeight={800} sx={{ ml: 2, minWidth: 60, textAlign: 'right' }}>
                      {statusData.count.toLocaleString()}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3, borderRadius: 3,
              border: '1px solid',
              borderColor: '#F0F2F1',
              boxShadow: '0 1px 2px rgba(26, 58, 52, 0.04)',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Recent registrations
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Last 7 days
              </Typography>
            </Box>
            <Box>
              {data.recent_activity.length > 0 ? (
                data.recent_activity.slice(0, 7).map((activity) => (
                  <Box
                    key={activity.date}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 1.25,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:last-child': { borderBottom: 'none' },
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {new Date(activity.date).toLocaleDateString('en-IN', {
                        month: 'short',
                        day: 'numeric',
                        weekday: 'short',
                      })}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          flex: 1,
                          height: 6,
                          width: 80,
                          borderRadius: 999,
                          backgroundColor: 'primary.light',
                          overflow: 'hidden',
                          position: 'relative',
                        }}
                      >
                        <Box
                          sx={{
                            height: '100%',
                            borderRadius: 999,
                            background: GRADIENTS.teal.bg,
                            width: `${Math.min(100, activity.registrations * 10)}%`,
                          }}
                        />
                      </Box>
                      <Typography fontWeight={800} sx={{ minWidth: 30, textAlign: 'right' }}>
                        {activity.registrations}
                      </Typography>
                    </Box>
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No recent registrations
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
